import { useSmartRelatedGifsQuery } from "queries";
import { AxiosError } from "axios";
import { IErrorData, IGif, INiche, INicheMap, IUser } from "services/types";
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IUserList } from "components/feed/GifList";
import { useWatchBoostedGifs } from "../hooks/useWatchBoostedGifs";
import { filterGifsList } from "utils/filterGifsList";
import { mixWithBoosted } from "utils/boost";
import { filterUsersList } from "utils/filterUsersList";
import { filterNichesList } from "utils/filterNichesList";
import { debugLoadingGifs } from "utils/bugSnagHelper";
import { TrendingTagsPlaceholder } from "components/UI/Skeleton/Skeleton";
import { RelatedTags } from "components/RelatedTags";
import { TrendingNiches } from "components/widgets/TrendingNiches";
import { FeedAds } from "components/ads/FeedAds";
import { MobileContentPageScrollKeys } from "components/ads/config/units/contentPageScrollMobile";
import { DesktopContentPageScrollKeys } from "components/ads/config/units/contentPageScrollDesktop";
import { PreviewFeed } from "components/feed/PreviewFeed";
import InjectionFeed from "components/feed/InjectionFeed";
import { ViewRegion } from "utils/metrics";
import { relToContext } from "utils/strings";

interface IProps {
  gif: IGif;
  user?: IUser | null;
  niches?: INiche[];
  rel?: string;
  order?: string;
  verified: boolean;
}

const MAX_PAGES = 5;

const RELATED_TAGS_POSITION = 0;
const NICHES_POSITION = 8;

const PRIVATE_COLLECTION = "PrivateCollection";
const COLLECTION_NOTFOUND = "CollectionNotFound";

export const RelatedFeed = (props: IProps) => {
  const [page, setPage] = useState<number>(1);
  const [getRelatedError, setRelatedError] = useState<string>("");

  const previousGifs = useRef<IGif[]>([]);
  const previousPages = useRef(1);
  const previousUsers = useRef<IUserList>({});
  const previousNiches = useRef<INicheMap>({});

  const isCollection = props.rel ? props.rel.match(/^c:(.+)/) : null;

  const { data, isSuccess, isError, isLoading, error: relatedGifError } = useSmartRelatedGifsQuery({
    gif: props.gif.id,
    rel: props.rel ?? "",
    page,
    verified: props.verified,
    order: props.order
  });

  const { boostedGifs, boostedUsers } = useWatchBoostedGifs({
    rel: props.rel,
    gifId: props.gif.id,
  });

  const fetchMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const gifs = useMemo(() => {
    if (data?.gifs) {
      const newGifs = data.gifs.filter(gif => props.gif.id !== gif.id);
      const filteredGifs = filterGifsList(previousGifs.current, newGifs);

      previousGifs.current = mixWithBoosted({
        gifs: filteredGifs,
        boostedPostData: boostedGifs,
        tiles: false,
      });
    }

    return previousGifs.current;
  }, [data, boostedGifs]);

  const feed = useMemo(() => {
    if (gifs.length > 0 || !isCollection) {
      return [{ ...props.gif, aliasId: props.gif.id }, ...gifs];
    }

    return [];
  }, [gifs]);

  const pages = useMemo(() => {
    if (isSuccess && data?.pages) {
      previousPages.current = Math.min(MAX_PAGES, data.pages);
    }

    return previousPages.current;
  }, [data]);

  const hasMore = useMemo(() => page < pages, [page, pages]);

  const users = useMemo(() => {
    if (props.user) {
      previousUsers.current = filterUsersList(previousUsers.current, [props.user]);
    }

    if (boostedUsers) {
      previousUsers.current = filterUsersList(previousUsers.current, boostedUsers);
    }

    if (data?.users) {
      previousUsers.current = filterUsersList(previousUsers.current, data.users);
    }

    return previousUsers.current;
  }, [data, props.gif, boostedUsers]);

  const niches = useMemo(() => {
    if (props.niches) {
      previousNiches.current = filterNichesList(previousNiches.current, props.niches);
    }

    if (data?.niches) {
      previousNiches.current = filterNichesList(previousNiches.current, data.niches);
    }

    return previousNiches.current;
  }, [data, props.niches]);

  const tags = useMemo(() => {
    return data?.tags ? data.tags : [];
  }, [data]);

  // TODO: debug 'error loading gif'
  useEffect(() => {
    if (isError) {
      debugLoadingGifs(relatedGifError as AxiosError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    const error = relatedGifError as AxiosError<IErrorData>;
    const errorCode = error?.response?.data?.error?.code ;

    if (errorCode === PRIVATE_COLLECTION || errorCode === COLLECTION_NOTFOUND) {
      setRelatedError(errorCode);
    }

  }, [relatedGifError]);

  // placeholder to prevent CLS issue that occurs when injector apply
  const injectPlaceholders = useCallback((pos: number) => {
    const children: ReactNode[] = [];

    if (pos === RELATED_TAGS_POSITION && isCollection) {
      children.push(<TrendingTagsPlaceholder key={`TrendingTagsPlaceholder-${pos}`} />);
    }

    return <>{children}</>;
  }, []);

  const injector = useCallback((pos: number): JSX.Element => {
    const children: ReactNode[] = [];

    if (pos === RELATED_TAGS_POSITION && tags.length > 0) {
      children.push(<RelatedTags key="RelatedTags" tags={tags} />);
    }

    if (pos === NICHES_POSITION) {
      children.push(<TrendingNiches key="TrendingNiches" />);
    }

    children.push(
      <FeedAds
        key={`feedAds-${pos}`}
        position={pos}
        mobileAds={MobileContentPageScrollKeys}
        desktopAds={DesktopContentPageScrollKeys}
      />
    );

    return <Fragment>{children}</Fragment>;
  }, [tags]);

  const renderContent = () => {
    if (isError && !relatedGifError) {
      return <p className="error">Error loading gifs. Please refresh the page.</p>;
    }

    if (getRelatedError === PRIVATE_COLLECTION) {
      return <p className="error">You cannot access this private collection.</p>;
    }

    if (getRelatedError === COLLECTION_NOTFOUND) {
      return <p className="error">Collection not found.</p>;
    }

    return (
      <PreviewFeed>
        <InjectionFeed
          gifs={feed}
          users={users}
          niches={niches}
          source={ViewRegion.Related}
          sourceId={relToContext(props.rel)}
          injector={injector}
          isLoading={isLoading}
          fetchMore={fetchMore}
          hasMore={hasMore}
          injectPlaceholders={injectPlaceholders}
        />
      </PreviewFeed>
    );
  };

  return (
    <Fragment>
      {renderContent()}
    </Fragment>
  );
};
